<template>
  <div class="spotlight-carousel-wrap">
    <p class="text-center text-muted small mb-3 font-weight-bold text-uppercase">
      Cursos e concursos em destaque
    </p>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando…</span>
      </div>
    </div>

    <div v-else-if="!slides.length" class="alert alert-light border text-center mb-0" role="status">
      Não foi possível carregar os cursos e concursos no momento.
      <a :href="links.concursosPublico" class="d-inline-block mt-2" target="_blank" rel="noopener">Ver concursos</a>
      ·
      <a :href="links.cursosLanding" class="d-inline-block mt-2" target="_blank" rel="noopener">Ver cursos</a>
    </div>

    <div
      v-else
      :id="carouselId"
      class="carousel slide spotlight-carousel"
      data-ride="carousel"
      data-interval="6000"
    >
      <ol class="carousel-indicators">
        <li
          v-for="(slide, index) in slides"
          :key="'ind-' + slide.key"
          :data-target="'#' + carouselId"
          :data-slide-to="index"
          :class="{ active: index === 0 }"
        />
      </ol>

      <div class="carousel-inner rounded">
        <div
          v-for="(slide, index) in slides"
          :key="slide.key"
          :class="['carousel-item', { active: index === 0 }]"
        >
          <a :href="slide.href" class="spotlight-link d-block" :target="slide.external ? '_blank' : '_self'" rel="noopener noreferrer">
            <div class="row no-gutters align-items-stretch spotlight-row">
              <div
                v-if="slide.image"
                class="col-md-5 spotlight-img-col"
              >
                <img
                  class="spotlight-img"
                  :src="slide.image"
                  :alt="slide.alt"
                >
              </div>
              <div :class="['col-md-7 spotlight-body', { 'col-12': !slide.image }]">
                <span class="badge badge-pill mb-2" :class="slide.kind === 'curso' ? 'badge-primary' : 'badge-dark'">
                  {{ slide.kind === 'curso' ? 'Curso' : 'Concurso' }}
                </span>
                <h3 class="h5 spotlight-title">
                  {{ slide.title }}
                </h3>
                <p v-if="slide.subtitle" class="text-muted small mb-2">
                  {{ slide.subtitle }}
                </p>
                <p v-if="slide.excerpt" class="spotlight-excerpt small d-none d-md-block" v-html="slide.excerpt" />
                <span class="text-primary small font-weight-bold">Saiba mais →</span>
              </div>
            </div>
          </a>
        </div>
      </div>

      <a class="carousel-control-prev" :href="'#' + carouselId" role="button" data-slide="prev">
        <span class="carousel-control-prev-icon" aria-hidden="true"></span>
        <span class="sr-only">Anterior</span>
      </a>
      <a class="carousel-control-next" :href="'#' + carouselId" role="button" data-slide="next">
        <span class="carousel-control-next-icon" aria-hidden="true"></span>
        <span class="sr-only">Próximo</span>
      </a>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import links from '../../../consts/links.js'

const MAX_EACH = 5
const MAX_SLIDES = 10

function stripHtml (html) {
  if (!html || typeof html !== 'string') return ''
  const t = document.createElement('div')
  t.innerHTML = html
  return (t.textContent || t.innerText || '').trim()
}

function truncate (text, len) {
  if (!text) return ''
  const t = text.replace(/\s+/g, ' ')
  return t.length > len ? t.slice(0, len) + '…' : t
}

export default {
  name: 'HomeSpotlightCarousel',
  data () {
    return {
      links,
      slides: [],
      loading: true,
      carouselId: 'spotlightHomeCarousel'
    }
  },
  mounted () {
    this.loadSlides()
  },
  methods: {
    async loadSlides () {
      this.loading = true
      let cursos = []
      let concursos = []

      try {
        const resCursos = await axios.get(`${links.apiCursos}/cursos-destaque`, { timeout: 12000 })
        if (resCursos.data && Array.isArray(resCursos.data)) {
          cursos = resCursos.data.slice(0, MAX_EACH)
        }
      } catch (e) {
        try {
          const resAlt = await axios.get(`${links.apiCursos}/cursos`, { timeout: 12000 })
          if (resAlt.data && Array.isArray(resAlt.data)) {
            cursos = resAlt.data.slice(0, MAX_EACH)
          }
        } catch (e2) {
          console.warn('HomeSpotlightCarousel: cursos', e2)
        }
      }

      try {
        const resPs = await this.$axios.get('/ps/concurso', { timeout: 12000 })
        const list = (resPs.data && Array.isArray(resPs.data)) ? resPs.data : []
        concursos = list
          .slice()
          .sort((a, b) => (b.id || 0) - (a.id || 0))
          .slice(0, MAX_EACH)
      } catch (e) {
        console.warn('HomeSpotlightCarousel: concursos', e)
      }

      this.slides = this.mergeSlides(cursos, concursos)
      this.loading = false
    },

    mergeSlides (cursos, concursos) {
      const out = []
      const cursoSlides = cursos.map((c) => {
        const nomeLink = c.nome_link || String(c.id || '')
        const href = nomeLink
          ? `${links.cursosLanding}/#/sobre-o-curso/${encodeURIComponent(nomeLink)}`
          : links.cursosLanding
        const excerpt = truncate(stripHtml(c.objetivo || c.publico_alvo || ''), 160)
        return {
          kind: 'curso',
          key: `curso-${c.id}`,
          title: c.nome || 'Curso',
          subtitle: c.carga_horaria ? `${c.carga_horaria} horas` : '',
          excerpt,
          image: c.extensao_banner_vertical || null,
          alt: c.nome ? `Banner do curso ${c.nome}` : 'Curso',
          href,
          external: true
        }
      })

      const concursoSlides = concursos.map((co) => {
        const href = co.link_area_candidato && String(co.link_area_candidato).startsWith('http')
          ? co.link_area_candidato
          : `${links.concursosPublico}`
        const sub = [co.prefeitura, co.data_prazo_fim ? `Inscrições até ${this.formatDate(co.data_prazo_fim)}` : '']
          .filter(Boolean)
          .join(' · ')
        return {
          kind: 'concurso',
          key: `concurso-${co.id}`,
          title: co.titulo || 'Processo seletivo',
          subtitle: sub,
          excerpt: '',
          image: null,
          alt: '',
          href,
          external: true
        }
      })

      let i = 0
      let j = 0
      while (out.length < MAX_SLIDES && (i < cursoSlides.length || j < concursoSlides.length)) {
        if (i < cursoSlides.length) {
          out.push(cursoSlides[i])
          i++
        }
        if (out.length >= MAX_SLIDES) break
        if (j < concursoSlides.length) {
          out.push(concursoSlides[j])
          j++
        }
      }

      return out
    },

    formatDate (d) {
      if (!d) return ''
      try {
        const date = new Date(d)
        if (isNaN(date.getTime())) return String(d)
        const dia = String(date.getDate()).padStart(2, '0')
        const mes = String(date.getMonth() + 1).padStart(2, '0')
        const ano = date.getFullYear()
        return `${dia}/${mes}/${ano}`
      } catch (e) {
        return String(d)
      }
    }
  }
}
</script>

<style scoped>
.spotlight-carousel-wrap {
  width: 100%;
}

.spotlight-carousel {
  background: #f7fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(2, 34, 91, 0.08);
}

.spotlight-link {
  color: inherit;
  text-decoration: none;
}

.spotlight-link:hover {
  text-decoration: none;
  color: inherit;
}

.spotlight-row {
  min-height: 200px;
}

.spotlight-img-col {
  background: #e9ecef;
}

.spotlight-img {
  width: 100%;
  height: 100%;
  min-height: 200px;
  max-height: 280px;
  object-fit: cover;
}

.spotlight-body {
  padding: 1.25rem 1.5rem;
  text-align: left;
}

.spotlight-title {
  font-weight: 700;
  color: #222;
}

.spotlight-excerpt {
  color: #555;
  line-height: 1.4;
}

.spotlight-carousel .carousel-control-prev,
.spotlight-carousel .carousel-control-next {
  filter: invert(0.35);
  width: 8%;
}

@media (max-width: 767px) {
  .spotlight-body {
    padding: 1rem;
  }
}
</style>
